import React from 'react';
import { connect } from '../redux/store';
import { compose, lifecycle, withHandlers, withState } from 'recompose';
import { CoreForm, I18n } from '@kineticdata/react';
import { Modal, ModalBody } from 'reactstrap';
import { is, Map } from 'immutable';
import { H, ReportSiteIssueWrapper } from '@kineticdata/bundle-common';
/**
 * This is a reimplementation of the ModalFormContainer component from
 * @kineticdata/bundle-common. It uses the connect function and the redux
 * actions imported from the common library so that it can reuse the redux
 * state and saga logic from common.
 *
 * It adds a Layout component top the CoreForm renderer, which using the common
 * ModalFormContainer doesn't allow.
 */

import { connect as commonConnect } from '@kineticdata/bundle-common/lib/redux/store';
import { actions as commonActions } from '@kineticdata/bundle-common/lib/redux/modules/modalForm';

const Layout = connect(state => ({
  reportForm: H.selectReportSiteIssueForm(state),
}))(({ form, content, reportForm }) => (
  <ReportSiteIssueWrapper form={form} reportForm={reportForm}>
    {content}
  </ReportSiteIssueWrapper>
));

const CustomModalFormContainerComponent = ({
  form,
  isCompleted,
  handleCompleted,
  handleClosed,
}) => {
  if (!form.formSlug && !form.submissionId) {
    return null;
  }
  const context = form.submissionId
    ? undefined
    : form.kappSlug
      ? `kapps.${form.kappSlug}.forms.${form.formSlug}`
      : `datastore.forms.${form.formSlug}`;
  return (
    <I18n context={context} submissionId={form.submissionId}>
      <Modal isOpen toggle={handleClosed} size="lg">
        <div className="modal-header">
          <h4 className="modal-title">
            <button
              type="button"
              className="btn btn-link"
              onClick={handleClosed}
            >
              <I18n>Close</I18n>
            </button>
            <span>
              <I18n>{form.title || ''}</I18n>
            </span>
          </h4>
        </div>
        <ModalBody>
          {isCompleted ? (
            <div className="modal-confirmation">
              <p>
                <I18n>
                  {form.confirmationMessage || 'Your form has been submitted.'}
                </I18n>
              </p>
            </div>
          ) : form.submissionId ? (
            <CoreForm
              submission={form.submissionId}
              loaded={form.loaded}
              updated={form.updated}
              completed={handleCompleted}
              layoutComponent={Layout}
            />
          ) : (
            <CoreForm
              kapp={form.kappSlug || 'datastore'}
              form={form.formSlug}
              values={form.values}
              loaded={form.loaded}
              created={form.created}
              updated={form.updated}
              completed={handleCompleted}
              layoutComponent={Layout}
            />
          )}
        </ModalBody>
      </Modal>
    </I18n>
  );
};

export const mapStateToProps = state => {
  return {
    form: state.modalForm.form,
    isCompleted: state.modalForm.formIsCompleted,
  };
};

const mapDispatchToProps = {
  closeForm: commonActions.closeForm,
  completeForm: commonActions.completeForm,
};

export const CustomModalFormContainer = compose(
  commonConnect(mapStateToProps, mapDispatchToProps),
  withState('timeoutId', 'setTimeoutId', null),
  withHandlers({
    handleCompleted: props => (submission, actions) => {
      if (typeof props.form.completed === 'function') {
        props.form.completed(submission, actions);
      }
      actions.stop();
      props.completeForm();
    },
    handleClosed: props => event => {
      if (event) event.stopPropagation();
      props.closeForm();
    },
  }),
  lifecycle({
    componentDidUpdate(prevProps) {
      // When form is completed and duration option is provided, set timeout to close modal
      if (
        this.props.isCompleted &&
        !prevProps.isCompleted &&
        typeof this.props.form.duration === 'number'
      ) {
        this.props.setTimeoutId(
          setTimeout(this.props.closeForm, this.props.form.duration),
        );
      }

      // When modal is closed or form changes, clear timeout
      if (
        !is(Map(this.props.form), Map(prevProps.form)) ||
        (!this.props.isCompleted && prevProps.isCompleted)
      ) {
        this.props.setTimeoutId(null);
        clearTimeout(this.props.timeoutId);
      }
    },
    componentWillUnmount() {
      clearTimeout(this.props.timeoutId);
    },
  }),
)(CustomModalFormContainerComponent);
